import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, Navigate } from "react-router-dom";
import { listingPath, categoryPath } from "@/lib/paths";
import VerifiedBadge from "@/components/VerifiedBadge";
import { api, absUrl } from "@/lib/api";
import FreeListingDetail from "@/components/FreeListingDetail";
import { MapPin, Phone, Mail, Globe, Star, X, Sparkles, Loader2, ShieldCheck, Package, Clock, Building2, Check, AlertCircle, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useAuth } from "@/contexts/AuthContext";
import ViewBadge from "@/components/ViewBadge";
import SEO from "@/components/SEO";
import { listingSeo } from "@/lib/seo";

function ClaimListingModal({ open, onClose, listing, auto = false }) {
  // auto=true → arrived via our unique claim link: fire the magic link to the email on file, no input box.
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { status, message, email_hint }
  const [autoState, setAutoState] = useState("idle"); // idle | sending | done
  const listingRef = listing?.slug || listing?.id;

  useEffect(() => {
    if (!open || !auto || autoState !== "idle" || !listingRef) return;
    setAutoState("sending");
    api.post("/auth/claim-listing", { listing_id: listingRef })
      .then(({ data }) => setResult(data))
      .catch((err) => setResult({ status: "send_failed", message: err.response?.data?.detail || "We couldn't send the email just now — please try again in a moment." }))
      .finally(() => setAutoState("done"));
  }, [open, auto, autoState, listingRef]);

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/claim-listing", { email: email.trim(), claim_listing_ref: listingRef });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send magic link — please try again.");
    } finally { setBusy(false); }
  };
  const showForm = !result || result.status === "no_email" || result.status === "send_failed" || result.status === "email_in_use";
  const sent = result && (result.status === "sent" || result.status === "generic");
  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="claim-listing-modal" data-mode={auto ? "mailer" : "organic"} onClick={onClose}>
      <div className="bg-white max-w-md w-full p-8 border-t-4 border-[#B35C4A]" onClick={(e) => e.stopPropagation()}>
        <div className="tracking-overline text-[#B35C4A]">Claim this listing</div>
        <h3 className="font-serif text-2xl text-[#11211A] mt-1">{listing.company || listing.title}</h3>

        {auto && autoState !== "done" && (
          <div className="mt-5 flex items-center gap-2 text-sm text-[#4A5550]" data-testid="claim-auto-sending"><Loader2 className="w-4 h-4 animate-spin" /> Sending your magic link to the email on file…</div>
        )}

        {sent && (
          <div className="mt-5 text-sm text-[#4A5550] leading-relaxed" data-testid="claim-sent-message">
            {result.status === "sent" && result.email_hint
              ? <p>We've sent your magic link to <strong data-testid="claim-sent-email-hint">{result.email_hint}</strong>{auto ? " — the email on file for this listing" : ""}. Check your inbox (and spam); the link is valid for 7 days.</p>
              : <p>{result.message}</p>}
            <p className="mt-3">Set a password to activate your account. The "Unclaimed" badge will be removed automatically.</p>
            <button onClick={onClose} className="btn-primary mt-5" data-testid="claim-modal-close">Got it</button>
          </div>
        )}

        {result && result.status === "already_claimed" && (
          <div className="mt-5 text-sm text-[#4A5550] leading-relaxed" data-testid="claim-already-claimed">
            <p>{result.message}</p>
            <div className="flex gap-2 mt-5">
              <Link to="/login" className="btn-primary flex-1 justify-center" data-testid="claim-signin-link">Sign in</Link>
              <button onClick={onClose} className="btn-ghost flex-1 justify-center" data-testid="claim-modal-close">Close</button>
            </div>
          </div>
        )}

        {showForm && (!auto || autoState === "done") && (
          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="claim-email-form">
            {result?.message && <p className="text-sm text-[#B35C4A] leading-relaxed" data-testid="claim-auto-fallback-message">{result.message}</p>}
            <p className="text-sm text-[#4A5550] leading-relaxed">Enter your business email and we'll send you a one-time magic link to set your password and take control of this listing. Your account will use the email you enter here.</p>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Your business email *</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={listing.contact_email || "you@your-company.com"}
                className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm"
                data-testid="claim-email-input"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center" data-testid="claim-cancel">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center" data-testid="claim-submit">
                {busy ? "Sending…" : "Send magic link"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function RFQModal({ open, onClose, listing }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_website: "",
    hotel: "",
    property_type: "",
    customer_phone: "",
    product_name: "",
    customer_company: "",
    quantity: "",
    target_delivery: "",
    delivery_location: "",
    material: "",
    fabric_spec: "",
    order_frequency: "",
    measure_unit: "cm",
    size: "",
    colour: "",
    packaging: "",
    certifications: "",
    logo_required: false,
    logo_url: "",
    pms_colours: "",
    notes: "",
    send_to_similar: false,
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        hotel: f.hotel || user.company || "",
        customer_company: f.customer_company || user.company || "",
      }));
    }
    if (open) setStep(1);
  }, [open, user]);

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingLogo(false); }
  };

  const draftWithAI = async () => {
    if (!form.customer_name) {
      toast.error("Enter your name first");
      return;
    }
    setDrafting(true);
    try {
      const specParts = [];
      if (form.material) specParts.push(`Material: ${form.material}`);
      if (form.fabric_spec) specParts.push(`Fabric weight / GSM: ${form.fabric_spec}`);
      if (form.size) specParts.push(`Size: ${form.size} ${form.measure_unit}`);
      if (form.colour) specParts.push(`Colour: ${form.colour}`);
      if (form.packaging) specParts.push(`Packaging: ${form.packaging}`);
      if (form.certifications) specParts.push(`Certifications: ${form.certifications}`);
      if (form.order_frequency) specParts.push(`Order frequency: ${form.order_frequency}`);
      if (form.logo_required) specParts.push(`Custom branding required${form.pms_colours ? ` (PMS: ${form.pms_colours})` : ""}`);
      const seed = form.notes && form.notes.trim() ? form.notes : `Quote request for ${listing.title}. Quantity: ${form.quantity}. Delivery to ${form.delivery_location || form.hotel} by ${form.target_delivery}.`;
      const enrichedNotes = specParts.length ? `${seed}\n\nProduct specification:\n- ${specParts.join("\n- ")}` : seed;

      const { data } = await api.post("/ai/draft-rfq", {
        listing_title: listing.title,
        customer_name: form.customer_name,
        customer_company: form.hotel || form.customer_company,
        quantity: form.quantity,
        required_by: form.target_delivery,
        notes: enrichedNotes,
      });
      setForm((f) => ({ ...f, notes: data.draft }));
      toast.success("AI draft generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to draft");
    } finally { setDrafting(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const autoNotes = form.notes && form.notes.trim()
        ? form.notes
        : `Hi,\n\nI'd like a quote for ${listing.title}.\nQuantity: ${form.quantity}\nDelivery location: ${form.delivery_location || form.hotel || "TBD"}\nTarget delivery: ${form.target_delivery}\n\nPlease share pricing, MOQ range, and lead-time range.\n\nThanks,\n${form.customer_name}`;
      const { data } = await api.post("/rfq", { listing_id: listing.id, ...form, notes: autoNotes });
      const extra = data.similar_count ? ` + ${data.similar_count} similar supplier${data.similar_count > 1 ? "s" : ""}` : "";
      if (data.email_status === "sent") toast.success(`RFQ sent to supplier${extra}`);
      else if (data.email_status?.startsWith("skipped")) toast.success(`RFQ recorded${extra} (email not configured)`);
      else if (data.email_status === "failed") toast.success(`RFQ recorded${extra} (email delivery pending)`);
      else toast.success("RFQ submitted");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Failed to send");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rfq-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8ECE9]">
          <div>
            <div className="tracking-overline mb-1">Request Quote · Step {step} of 2</div>
            <div className="font-serif text-xl text-[#11211A]">{listing.title}</div>
          </div>
          <button onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A]" data-testid="rfq-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center px-6 py-3 bg-[#F0EFEA] border-b border-[#E8ECE9]" data-testid="rfq-stepper">
          <div className={`flex items-center gap-2 text-xs font-medium ${step === 1 ? "text-[#264839]" : "text-[#7D8C85]"}`} data-testid="rfq-step-indicator-1">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${step === 1 ? "bg-[#264839] text-white" : "bg-[#C4CFC9] text-white"}`}>1</span>
            The basics · 30 seconds
          </div>
          <div className="flex-1 h-px bg-[#C4CFC9] mx-4" />
          <div className={`flex items-center gap-2 text-xs font-medium ${step === 2 ? "text-[#264839]" : "text-[#7D8C85]"}`} data-testid="rfq-step-indicator-2">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${step === 2 ? "bg-[#264839] text-white" : "bg-[#C4CFC9] text-white"}`}>2</span>
            Specs & AI polish · optional
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {step === 1 && (
          <div data-testid="rfq-step-1">
          <div className="mb-4 p-3 bg-[#F0EFEA] border-l-4 border-[#B35C4A]" data-testid="rfq-product-name-wrap">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#B35C4A] mb-1">What are you looking for? *</label>
            <input
              required
              value={form.product_name}
              onChange={set("product_name")}
              placeholder="e.g. 5-Star Hotel Towels"
              className="w-full px-3 py-2 text-base font-serif border border-[#C4CFC9] rounded-sm bg-white"
              data-testid="rfq-product-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Your name *</label>
              <input required value={form.customer_name} onChange={set("customer_name")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Email *</label>
              <input required type="email" value={form.customer_email} onChange={set("customer_email")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-email" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Hotel / Property *</label>
              <input required value={form.hotel} onChange={set("hotel")} placeholder="e.g. The Grand Palace, London" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-hotel" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Property type *</label>
              <select required value={form.property_type} onChange={set("property_type")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="rfq-property-type">
                <option value="">— Select —</option>
                <option value="Hotel">Hotel</option>
                <option value="Resort">Resort</option>
                <option value="Motel / Inn">Motel / Inn</option>
                <option value="Boutique Hotel">Boutique Hotel</option>
                <option value="Serviced Apartments">Serviced Apartments</option>
                <option value="Short-Stay / Vacation Rental">Short-Stay / Vacation Rental</option>
                <option value="Bed & Breakfast">Bed & Breakfast</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Quantity / rooms *</label>
              <input required value={form.quantity} onChange={set("quantity")} placeholder="e.g. 120 rooms · 500 units" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-quantity" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Delivery location *</label>
              <input required value={form.delivery_location} onChange={set("delivery_location")} placeholder="e.g. London, UK" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-delivery-location" />
              {form.delivery_location && (() => {
                const loc = form.delivery_location.toLowerCase();
                const listingStates = (listing.state_codes || []).map((s) => (s || "").toLowerCase());
                const listingCountry = (listing.country || "").toLowerCase();
                const listingCountryCode = (listing.country_code || "").toLowerCase();
                const listingCity = (listing.city || "").toLowerCase();
                // Very forgiving match — buyer types free-form text
                const matches =
                  (listingCity && loc.includes(listingCity)) ||
                  (listingCountry && loc.includes(listingCountry)) ||
                  (listingCountryCode && (loc.includes(` ${listingCountryCode}`) || loc.endsWith(listingCountryCode) || loc.startsWith(listingCountryCode))) ||
                  listingStates.some((s) => s && (loc.includes(s.toLowerCase()) || loc.includes((s || "").toUpperCase())));
                return (
                  <div className={`mt-1.5 flex items-center gap-1.5 text-xs ${matches ? "text-[#264839]" : "text-[#7D8C85]"}`} data-testid="rfq-ships-to-check">
                    {matches ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>Great — this supplier ships to <strong>{form.delivery_location}</strong></span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>Confirm with supplier — their listed coverage may differ.</span>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Timeframe *</label>
              <select required value={form.target_delivery} onChange={set("target_delivery")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="rfq-target-delivery">
                <option value="">— Select —</option>
                <option value="urgent">ASAP (within 2 weeks)</option>
                <option value="1_month">Within 1 month</option>
                <option value="1_3_months">1 – 3 months</option>
                <option value="3_6_months">3 – 6 months</option>
                <option value="planning">Planning / no deadline</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Phone (optional)</label>
              <input value={form.customer_phone} onChange={set("customer_phone")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-phone" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Web address (optional)</label>
              <input value={form.customer_website} onChange={set("customer_website")} placeholder="e.g. grand-palace-hotel.com" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-website" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Company (optional)</label>
              <input value={form.customer_company} onChange={set("customer_company")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-company" />
            </div>
          </div>
          <div className="flex justify-between items-center pt-4">
            <p className="text-xs text-[#7D8C85]">You can send this quick RFQ now, or add detailed specs on the next step.</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!form.customer_name || !form.customer_email || !form.hotel || !form.property_type || !form.quantity || !form.delivery_location || !form.target_delivery) {
                    toast.error("Please fill in the required basics (property type, location, quantity, timeframe) before continuing.");
                    return;
                  }
                  setStep(2);
                }}
                className="btn-secondary"
                data-testid="rfq-step-1-next"
              >
                Add specs & polish with AI →
              </button>
            </div>
          </div>
          </div>
          )}

          {step === 2 && (
          <div data-testid="rfq-step-2" className="space-y-4">

          {/* Product spec block */}
          <div className="border border-[#E8ECE9] p-4 space-y-3">
            <div className="tracking-overline">Product specification</div>
            <p className="text-xs text-[#7D8C85] -mt-1">The more you provide, the more accurate the quote. All fields optional.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Material / Fabric</label>
                <input value={form.material} onChange={set("material")} placeholder="e.g. 100% Egyptian cotton" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-material" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Fabric weight / GSM</label>
                <input value={form.fabric_spec} onChange={set("fabric_spec")} placeholder="e.g. 600 gsm" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-fabric-weight" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Order frequency</label>
                <select value={form.order_frequency} onChange={set("order_frequency")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="rfq-order-frequency">
                  <option value="">— Select —</option>
                  <option value="one-off">One-off purchase</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                  <option value="ongoing">Ongoing / open PO</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Size / Dimensions</label>
                <input value={form.size} onChange={set("size")} placeholder="e.g. 70 x 140" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-size" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Measurement unit</label>
                <div className="flex gap-1" data-testid="rfq-measure-unit">
                  {["cm", "in", "mm"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setForm({ ...form, measure_unit: u })}
                      className={`flex-1 px-3 py-2 text-sm border ${form.measure_unit === u ? "bg-[#264839] text-white border-[#264839]" : "border-[#C4CFC9] text-[#4A5550]"}`}
                      data-testid={`rfq-measure-${u}`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Colour preference</label>
                <input value={form.colour} onChange={set("colour")} placeholder="e.g. white, cream, custom Pantone" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-colour" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Packaging</label>
                <input value={form.packaging} onChange={set("packaging")} placeholder="e.g. individually poly-bagged" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-packaging" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Certifications required</label>
                <input value={form.certifications} onChange={set("certifications")} placeholder="e.g. OEKO-TEX, Fair Trade, GOTS" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="rfq-certifications" />
              </div>
            </div>
          </div>

          {/* Branding block */}
          <div className="bg-[#F0EFEA] border border-[#E8ECE9] p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-[#11211A] font-medium">
              <input
                type="checkbox"
                checked={form.logo_required}
                onChange={(e) => setForm({ ...form, logo_required: e.target.checked })}
                data-testid="rfq-logo-required"
              />
              Custom branding required (logo, embroidery, print)
            </label>
            {form.logo_required && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Upload logo file</label>
                  <label className="inline-flex items-center gap-2 text-sm border border-[#C4CFC9] px-3 py-2 rounded-sm cursor-pointer hover:border-[#264839] w-full">
                    <span className="text-[#4A5550] truncate">{form.logo_url ? form.logo_url.split("/").pop() : "Choose file (PNG/SVG/JPG)"}</span>
                    <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="rfq-logo-file" />
                  </label>
                  {uploadingLogo && <div className="text-xs text-[#7D8C85] mt-1">Uploading…</div>}
                  {form.logo_url && !uploadingLogo && (
                    <div className="mt-2"><img src={absUrl(form.logo_url)} alt="logo preview" className="max-h-12" /></div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Brand PMS colours</label>
                  <input
                    value={form.pms_colours}
                    onChange={set("pms_colours")}
                    placeholder="e.g. Pantone 288 C, PMS 431 U"
                    className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm"
                    data-testid="rfq-pms-colours"
                  />
                  <div className="text-xs text-[#7D8C85] mt-1">Include specific Pantone codes so the supplier quotes accurately.</div>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-[#4A5550]">Your message <span className="text-[#7D8C85] font-normal">(optional — we'll build one from your basics if left blank)</span></label>
              <button type="button" onClick={draftWithAI} disabled={drafting} className="text-xs inline-flex items-center gap-1 text-[#264839] hover:text-[#1B3329] disabled:opacity-50" data-testid="rfq-ai-draft">
                {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {drafting ? "Drafting…" : "Improve with AI"}
              </button>
            </div>
            <textarea rows={7} value={form.notes} onChange={set("notes")} placeholder="Describe what you need. Click 'Improve with AI' to convert into a professional RFQ." className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm leading-relaxed" data-testid="rfq-notes" />
            <div className="text-xs text-[#7D8C85] mt-1">AI drafts a professional B2B RFQ with Claude Sonnet 4.5. A copy is BCC'd to your team for record-keeping.</div>
          </div>
          <label className="flex items-start gap-2 text-sm text-[#4A5550] bg-[#F0EFEA] p-3 border border-[#E8ECE9]">
            <input
              type="checkbox"
              checked={form.send_to_similar}
              onChange={(e) => setForm({ ...form, send_to_similar: e.target.checked })}
              className="mt-0.5"
              data-testid="rfq-send-to-similar"
            />
            <span>
              <span className="font-medium text-[#11211A]">Also send this RFQ to up to 3 similar suppliers</span>
              <br />
              <span className="text-xs text-[#7D8C85]">Get competitive quotes from other verified suppliers in the same category — one form, multiple quotes.</span>
            </span>
          </label>
          </div>
          )}

          <div className="flex justify-between items-center gap-3 pt-2 border-t border-[#E8ECE9] -mx-6 px-6 pt-4">
            {step === 2 ? (
              <button type="button" onClick={() => setStep(1)} className="btn-ghost" data-testid="rfq-step-back">← Back</button>
            ) : (
              <button type="button" onClick={onClose} className="btn-ghost" data-testid="rfq-cancel">Cancel</button>
            )}
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="rfq-submit">
              {submitting ? "Sending…" : (step === 1 ? "Send quick RFQ" : "Send detailed RFQ")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [item, setItem] = useState(null);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(searchParams.get("claim") === "1");
  // Mailer links land with ?claim=1 → pre-identified supplier (auto-send). The on-page button is the organic path.
  const [claimAuto, setClaimAuto] = useState(searchParams.get("claim") === "1");
  const [claimKey, setClaimKey] = useState(0); // remount the modal so a fresh organic open never shows a stale auto-send result
  const openClaimOrganic = () => { setClaimAuto(false); setClaimKey((k) => k + 1); setClaimOpen(true); };
  const [activeImg, setActiveImg] = useState("");
  const { formatRange } = useCurrency();
  const { user } = useAuth();

  useEffect(() => {
    if (item && (item.id === id || item.slug === id)) return;
    api.get(`/listings/${id}`).then(({ data }) => {
      setItem(data);
      setActiveImg(data.image_url || (data.gallery && data.gallery[0]) || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!item) return <div className="max-w-7xl mx-auto px-4 py-16 text-[#7D8C85]">Loading…</div>;
  if (item.slug && id !== item.slug) return <Navigate to={{ pathname: listingPath(item), search: window.location.search }} replace />;
  const seo = listingSeo(item);
  const seoTag = <SEO title={seo.title} description={seo.description} path={seo.path} image={seo.image} type="profile" />;
  if (!item.tier || item.tier === "free") {
    return (
      <>
        {seoTag}
        <FreeListingDetail item={item} user={user} onClaim={openClaimOrganic} />
        <ClaimListingModal key={`${claimAuto ? "mailer" : "organic"}-${claimKey}`} open={claimOpen} onClose={() => setClaimOpen(false)} listing={item} auto={claimAuto} />
      </>
    );
  }
  const gallery = (item.gallery && item.gallery.length > 0) ? item.gallery : (item.image_url ? [item.image_url] : []);
  // Same earned treatment as the homepage Fresh Picks card: verified Founding Supplier → green frame + "Verified & Featured".
  const earned = Boolean(item.founding_supplier && (item.is_verified || item.advertiser?.is_verified));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="listing-detail">
      {seoTag}
      <div className="text-sm text-[#7D8C85] mb-6">
        <Link to="/categories" className="hover:text-[#264839]">Categories</Link>
        {item.category && <> / <Link to={categoryPath(item.category)} className="hover:text-[#264839]">{item.category.name}</Link></>}
      </div>

      {/* Supplier header */}
      <div className={`bg-white p-6 md:p-8 mb-8 relative ${earned ? "border-2 border-[#264839] shadow-[0_14px_40px_-18px_rgba(38,72,57,0.5)]" : "border border-[#E8ECE9]"}`} data-testid="listing-header" data-earned={earned ? "true" : undefined}>
        {earned && (
          <div className="absolute -top-3 left-6 md:left-8 z-10" data-testid="verified-featured-badge-detail">
            <div className="bg-[#264839] text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1 shadow-md flex items-center gap-1.5" title="Verified supplier, featured for consistent buyer engagement">
              <BadgeCheck className="w-3.5 h-3.5" /> Verified &amp; Featured
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="w-20 h-20 bg-[#F0EFEA] flex items-center justify-center flex-shrink-0 border border-[#E8ECE9]">
            {item.logo_url ? (
              <img src={absUrl(item.logo_url)} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <Building2 className="w-8 h-8 text-[#7D8C85]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <div className="tracking-overline">{item.category?.name || ""}</div>
              {item.founding_supplier && !earned && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#B8860B] bg-[#FFFBEB] border border-[#B8860B]/30 px-2 py-0.5"
                  title="Founding Supplier — one of our first 50 launch partners. Locked-in pricing and 50-day pinned placement."
                  data-testid="founding-supplier-badge-detail"
                >
                  <Star className="w-3 h-3 fill-current" /> Founding Supplier
                </span>
              )}
              {item.is_verified && <VerifiedBadge />}
              {item.is_featured && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-white bg-[#B35C4A] px-2 py-0.5">
                  <Star className="w-3 h-3 fill-current" /> Featured
                </span>
              )}
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">{item.title}</h1>
            {item.advertiser && (
              <div className="text-sm text-[#4A5550] mt-2">
                by <span className="text-[#11211A] font-medium">{item.advertiser.company || item.advertiser.name}</span>
                {item.brand_profile && (
                  <span className="ml-2" data-testid="listing-brand-label">
                    · <span className="text-[#B35C4A] font-medium">{item.brand_profile.name}</span>
                  </span>
                )}
              </div>
            )}
            <TrustBadges badges={item.badges || item.advertiser?.badges} />
            {item.views_this_month >= 10 && <div className="mt-2"><ViewBadge views={item.views_this_month} listingId={item.id} size="md" /></div>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <button onClick={() => setRfqOpen(true)} className="btn-primary text-base px-6 py-3" data-testid="open-rfq-btn">
              <Sparkles className="w-4 h-4" /> Request Quote
            </button>
            {item.is_unclaimed && (
              <button
                type="button"
                onClick={openClaimOrganic}
                className="text-[10px] uppercase tracking-[0.15em] px-4 py-2 border border-[#B35C4A] text-[#B35C4A] bg-white hover:bg-[#B35C4A] hover:text-white transition-colors"
                data-testid="claim-listing-btn"
                title="If this is your business, click here to activate your listing via magic link."
              >
                Claim this listing
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/3] bg-[#E8ECE9] overflow-hidden">
            {activeImg && <img src={absUrl(activeImg)} alt={item.title} className="w-full h-full object-cover" />}
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(g)} className={`aspect-square overflow-hidden border ${activeImg === g ? "border-[#264839]" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                  <img src={absUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          {(() => {
            const label = formatRange(item.price_min, item.price_max, item.price);
            return label ? (
              <div className="mt-4">
                <div className="text-2xl text-[#11211A]" data-testid="detail-price">{label}</div>
                <div className="text-xs text-[#7D8C85] italic mt-1">Price varies by order volume and specifications.</div>
              </div>
            ) : null;
          })()}

          <div className="mt-6">
            <div className="tracking-overline mb-2">Description</div>
            <p className="text-[#4A5550] leading-relaxed whitespace-pre-wrap">{item.description}</p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="bg-[#F0EFEA] p-4">
              <div className="flex items-center gap-2 text-[#264839] mb-1">
                <Clock className="w-4 h-4" /><span className="text-xs uppercase tracking-[0.15em] font-semibold">Lead time</span>
              </div>
              <div className="text-[#11211A] font-serif text-lg" data-testid="listing-lead-time">
                {item.lead_time || <span className="text-sm text-[#7D8C85] italic font-sans">Ask supplier for a range (e.g. 2–4 weeks)</span>}
              </div>
            </div>
            <div className="bg-[#F0EFEA] p-4">
              <div className="flex items-center gap-2 text-[#264839] mb-1">
                <Package className="w-4 h-4" /><span className="text-xs uppercase tracking-[0.15em] font-semibold">MOQ</span>
              </div>
              <div className="text-[#11211A] font-serif text-lg" data-testid="listing-moq">
                {item.moq || <span className="text-sm text-[#7D8C85] italic font-sans">Ask supplier for a range (e.g. from 100 units)</span>}
              </div>
            </div>
          </div>

          {item.tags && item.tags.length > 0 && (
            <div className="mt-6">
              <div className="tracking-overline mb-2">Attributes</div>
              <div className="flex flex-wrap gap-2" data-testid="listing-tags-display">
                {item.tags.map((t, i) => (
                  <span key={i} className="text-xs bg-[#E8ECE9] text-[#264839] px-3 py-1 rounded-sm">{t}</span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            {(item.city || item.country) && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#264839] mt-0.5" />
                <div>
                  <div className="text-[#7D8C85] text-xs">Location</div>
                  <div className="text-[#11211A]">{[item.city, item.country].filter(Boolean).join(", ")}{item.country_code ? ` (${item.country_code})` : ""}</div>
                </div>
              </div>
            )}
            {item.contact_phone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#264839] mt-0.5" />
                <div>
                  <div className="text-[#7D8C85] text-xs">Phone</div>
                  <div className="text-[#11211A]">{item.contact_phone}</div>
                </div>
              </div>
            )}
            {item.contact_email && (
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-[#264839] mt-0.5" />
                <div>
                  <div className="text-[#7D8C85] text-xs">Email</div>
                  <div className="text-[#11211A] truncate">{item.contact_email}</div>
                </div>
              </div>
            )}
            {item.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#264839] mt-0.5" />
                <div>
                  <div className="text-[#7D8C85] text-xs">Website</div>
                  {(!item.tier || item.tier === "free")
                    ? <div className="text-[#11211A] break-all" data-testid="listing-website-text">{item.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</div>
                    : <a href={item.website} target="_blank" rel="noreferrer" className="text-[#264839] hover:underline" data-testid="listing-website-link">Visit</a>}
                </div>
              </div>
            )}
          </div>

          <button onClick={() => setRfqOpen(true)} className="btn-primary mt-10 w-full justify-center" data-testid="open-rfq-btn-bottom">
            <Sparkles className="w-4 h-4" /> Request Quote
          </button>
        </div>
      </div>

      <SupplierProducts supplierId={item.advertiser_id} tier={item.tier} />

      <RFQModal open={rfqOpen} onClose={() => setRfqOpen(false)} listing={item} />
      <ClaimListingModal key={`${claimAuto ? "mailer" : "organic"}-${claimKey}`} open={claimOpen} onClose={() => setClaimOpen(false)} listing={item} auto={claimAuto} />
    </div>
  );
}


const TRUST_BADGE_META = {
  details_confirmed:    { label: "Details Confirmed",    hint: "Listing claimed and confirmed by the supplier" },
  responsive_supplier:  { label: "Responsive Supplier",  hint: "Consistently replies to RFQs within 24 hrs" },
  fast_quote:           { label: "Fast Quote",           hint: "Delivers itemised quotes in 1–2 business days" },
  shipping_clarity:     { label: "Shipping Clarity",     hint: "Publishes shipping terms, lead times, and Incoterms upfront" },
};

function TrustBadges({ badges }) {
  if (!badges || typeof badges !== "object") return null;
  const active = Object.keys(TRUST_BADGE_META).filter((k) => badges[k]);
  if (active.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="trust-badges">
      {active.map((k) => (
        <span
          key={k}
          title={TRUST_BADGE_META[k].hint}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#264839] bg-[#E8ECE9] border border-[#264839]/20 px-2.5 py-1"
          data-testid={`trust-badge-${k.replace(/_/g,'-')}`}
        >
          <ShieldCheck className="w-3 h-3" /> {TRUST_BADGE_META[k].label}
        </span>
      ))}
    </div>
  );
}


function SupplierProducts({ supplierId, tier }) {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!supplierId) return;
    api.get(`/products?supplier_id=${supplierId}&limit=24`).then(({ data }) => {
      setProducts(data.items || []);
    }).catch(() => setProducts([])).finally(() => setLoaded(true));
  }, [supplierId]);
  // Show catalogue only when supplier has a Pro/Elite plan (or has products already).
  if (!loaded) return null;
  if (products.length === 0) return null;
  return (
    <section className="mb-12" data-testid="supplier-products">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="tracking-overline mb-1">Product catalogue</div>
          <h2 className="font-serif text-2xl text-[#11211A]">Featured products from this supplier</h2>
        </div>
        <div className="text-xs text-[#7D8C85]">{products.length} product{products.length === 1 ? "" : "s"}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            className="group bg-white border border-[#E8ECE9] hover:border-[#264839] transition block"
            data-testid={`product-card-${p.id}`}
          >
            <div className="aspect-[4/3] bg-[#F0EFEA] overflow-hidden">
              {p.image_url
                ? <img src={absUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                : <div className="w-full h-full flex items-center justify-center text-[#C4CFC9] text-xs">No image</div>}
            </div>
            <div className="p-5">
              <div className="font-serif text-lg text-[#11211A] leading-tight">{p.title}</div>
              {(p.price_min || p.price_max) && (
                <div className="text-xs text-[#4A5550] mt-2">
                  {p.price_min ? `$${p.price_min}` : ""}{p.price_max ? ` – $${p.price_max}` : ""} {p.currency}
                </div>
              )}
              {p.moq && <div className="text-xs text-[#7D8C85] mt-1">MOQ: {p.moq}</div>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
